import type { BaseOptionType } from "antd/lib/select"
import { useEffect, useState } from "react"

import {
  getGlobalFilterOptions,
  type FilterOptionsSurface
} from "../api/filter-options.services.ts"
import type { FilterDimension } from "../redux/globalFilters.ts"

export type GlobalFilterOptions = Record<FilterDimension, BaseOptionType[]>

const EMPTY_OPTIONS: GlobalFilterOptions = {
  project: [],
  pm: [],
  client: [],
  project_type: [],
  project_status: [],
  state: []
}

const normalizeOptions = (options: unknown): BaseOptionType[] => {
  if (!Array.isArray(options)) return []

  const flat: BaseOptionType[] = []
  for (const option of options) {
    if (!option || typeof option !== "object") continue

    const node = option as {
      label?: BaseOptionType["label"]
      value?: unknown
      children?: unknown
    }
    if (node.value !== undefined && node.value !== null) {
      const value = String(node.value)
      const label =
        typeof node.label === "string"
          ? node.label.trim() || value
          : node.label ?? value
      flat.push({ label, value })
    }
    flat.push(...normalizeOptions(node.children))
  }
  return flat
}

export interface UseGlobalFilterOptions {
  options: GlobalFilterOptions
  loading: boolean
}

// The landing page mounts inside the login/cold-boot request burst, where the
// first option fetch can hit before the JWT is rehydrated; retry with backoff so
// a transient auth-not-ready failure doesn't leave every dimension empty (which
// renders persisted filter values as raw ids).
const OPTIONS_MAX_RETRIES = 3
const OPTIONS_RETRY_BASE_MS = 300

export const useGlobalFilterOptions = (
  surface: FilterOptionsSurface = "dashboard"
): UseGlobalFilterOptions => {
  const [options, setOptions] = useState<GlobalFilterOptions>(EMPTY_OPTIONS)
  const [loading, setLoading] = useState(true)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | undefined

    const scheduleRetryOrSettle = () => {
      if (attempt >= OPTIONS_MAX_RETRIES) {
        setLoading(false)
        return
      }
      retryTimer = setTimeout(() => {
        if (!cancelled) setAttempt((n) => n + 1)
      }, OPTIONS_RETRY_BASE_MS * (attempt + 1))
    }

    void (async () => {
      try {
        const response = await getGlobalFilterOptions(surface)
        if (cancelled) return

        const data = response.data

        setOptions({
          project: normalizeOptions(data?.project),
          pm: normalizeOptions(data?.pm),
          client: normalizeOptions(data?.client),
          project_type: normalizeOptions(data?.project_type),
          project_status: normalizeOptions(data?.project_status),
          state: normalizeOptions(data?.state)
        })

        if (response.ok) setLoading(false)
        else scheduleRetryOrSettle()
      } catch {
        if (cancelled) return
        scheduleRetryOrSettle()
      }
    })()

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [attempt, surface])

  return { options, loading }
}
